import * as THREE from "three";
import { NeonTube } from "./NeonTube";
import { DEFAULT_LIGHTING, type DesignData, type LightingSettings } from "./types";

type Listener = () => void;

/**
 * The in-memory document: a named collection of tubes plus subscription so
 * UI panels can react to mutations without knowing about each tool.
 */
export class Design {
  readonly root: THREE.Group;
  private tubes: NeonTube[] = [];
  private listeners = new Set<Listener>();
  name = "untitled sign";
  background = "#07070b";
  backdrop = "brick";
  lighting: LightingSettings = { ...DEFAULT_LIGHTING };

  constructor() {
    this.root = new THREE.Group();
    this.root.name = "design-root";
  }

  get tubeCount(): number {
    return this.tubes.length;
  }

  getTubes(): readonly NeonTube[] {
    return this.tubes;
  }

  findById(id: string): NeonTube | undefined {
    return this.tubes.find((t) => t.id === id);
  }

  findByObject(obj: THREE.Object3D | null): NeonTube | undefined {
    let cur: THREE.Object3D | null = obj;
    while (cur) {
      const id = cur.userData?.tubeId as string | undefined;
      if (id) return this.findById(id);
      cur = cur.parent;
    }
    return undefined;
  }

  add(tube: NeonTube): void {
    this.tubes.push(tube);
    this.root.add(tube.group);
    this.emit();
  }

  remove(tube: NeonTube): void {
    const idx = this.tubes.indexOf(tube);
    if (idx === -1) return;
    this.tubes.splice(idx, 1);
    this.root.remove(tube.group);
    tube.dispose();
    this.emit();
  }

  clear(): void {
    for (const t of this.tubes.splice(0)) {
      this.root.remove(t.group);
      t.dispose();
    }
    this.emit();
  }

  /** Caller is expected to call this after mutating tube internals. */
  touch(): void {
    this.emit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Sum of every renderable tube's curve length, in scene units. */
  totalLength(): number {
    let sum = 0;
    for (const t of this.tubes) sum += t.length;
    return sum;
  }

  /** Axis-aligned bounding box of all tube control points. Empty if none. */
  boundingBox(): THREE.Box3 {
    const box = new THREE.Box3();
    for (const t of this.tubes) {
      for (const p of t.points) box.expandByPoint(p);
    }
    return box;
  }

  /** Per-frame hook so the App can drive subtle live effects (flicker). */
  tickAll(time: number): void {
    for (const t of this.tubes) t.tickFlicker(time);
  }

  private emit(): void {
    this.listeners.forEach((fn) => fn());
  }

  toData(): DesignData {
    return {
      version: 1,
      name: this.name,
      background: this.background,
      backdrop: this.backdrop,
      lighting: { ...this.lighting },
      tubes: this.tubes.map((t) => t.toData()),
    };
  }

  loadData(data: DesignData): void {
    this.clear();
    this.name = data.name ?? "untitled sign";
    this.background = data.background ?? "#07070b";
    this.backdrop = data.backdrop ?? "brick";
    this.lighting = { ...DEFAULT_LIGHTING, ...(data.lighting ?? {}) };
    for (const td of data.tubes ?? []) {
      const tube = new NeonTube(td);
      tube.rebuildMesh();
      this.tubes.push(tube);
      this.root.add(tube.group);
    }
    this.emit();
  }
}
